import type { PinnedArtifact } from "~/services/pinnedArtifacts";
import { usePinnedArtifactMutations, usePinnedArtifacts } from "./usePinnedArtifacts";

/** The pin, if any, that shares a session's artifact to its project. A pin
 *  still matches the session it was made from after a refresh moves it. */
export const findPin = (
  pins: PinnedArtifact[] | undefined,
  conversationId: string,
  artifactName: string
): PinnedArtifact | null =>
  pins?.find(
    (p) =>
      (p.source_conversation_id === conversationId && p.source_artifact_name === artifactName) ||
      (p.current_conversation_id === conversationId && p.current_artifact_name === artifactName)
  ) ?? null;

export interface ArtifactSharing {
  pinFor: (artifactName: string) => PinnedArtifact | null;
  // Null for a member who cannot curate the project's artifacts.
  toggle: ((artifactName: string, displayName: string) => Promise<void>) | null;
  // Whether the project's pins have loaded and no change is saving.
  isReady: boolean;
}

/** Sharing a session's artifacts to its project: one click shares an
 *  artifact, another unshares it. Sharing is a pin under the hood, so it
 *  carries the pin's rules: curating is owner-only, every member can read.
 *  Shares nothing, and fetches nothing, without a project and a session. */
export const useArtifactSharing = (
  projectUuid: string | null,
  conversationId: string | undefined,
  canShare: boolean
): ArtifactSharing => {
  const { pinnedArtifacts } = usePinnedArtifacts(projectUuid);
  const { pin, unpin, isMutating } = usePinnedArtifactMutations(projectUuid ?? "");
  const pinFor = (artifactName: string) =>
    conversationId ? findPin(pinnedArtifacts, conversationId, artifactName) : null;

  const toggle =
    canShare && projectUuid && conversationId
      ? async (artifactName: string, displayName: string) => {
          const existing = pinFor(artifactName);
          if (existing) {
            await unpin(existing);
            return;
          }
          await pin({
            conversation_id: conversationId,
            artifact_name: artifactName,
            display_name: displayName,
          });
        }
      : null;

  return {
    pinFor,
    toggle,
    isReady: pinnedArtifacts !== undefined && !isMutating,
  };
};
